import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { MdEdit } from "react-icons/md";
import { IoAddCircleSharp } from "react-icons/io5";
import { MdDeleteForever } from "react-icons/md";
import { useTable, useGlobalFilter, usePagination } from "react-table";
import Swal from "sweetalert2";
import SettingsService from "../services/SettingsService";
import Insert from "../popup/Insert";
import Update from "../popup/Update";
import { FaSearch } from "react-icons/fa"; // Import the search icon
import {
  DataGrid,
  Column,
  SearchPanel,
  FilterRow,
  Selection,
  Button,
  Editing
} from 'devextreme-react/data-grid'
import { useTranslation } from "react-i18next";
import { trHeader } from "../i18n/headerMap";
import {
  getDxDataGridInstance,
  getDxExportRows,
} from "../utility/devextremeGridInstance";
import TableExportButton from "../components/TableExportButton";

function TableForge({
  refresh,
  name,
  tableData,
  onSelectionChanged,
  selectedRowKeys: selectedRowKeysProp,
  onSelectedRowKeysChange,
  gridBelow = null,
  initialFilterColumn,
  onInitialFilterOverridden,
}) {
  const { t } = useTranslation();
  const dataGridRef = useRef(null);
  const filterApplySuppressUntilRef = useRef(0);
  const filterInitialAppliedRef = useRef(false);
  const lastKeysBeforeInsertRef = useRef(null);
  const pendingInsertFocusRef = useRef(null);

  useEffect(() => {
    const pending = pendingInsertFocusRef.current;
    if (!pending) return;

    const selectedTable = tablesAssociation.find((table) => table.name === name);
    const keyField = selectedTable?.id;
    if (!keyField) return;

    const rows = Array.isArray(tableData) ? tableData : [];
    const keysNow = rows
      .map((r) => (r && r[keyField] != null ? String(r[keyField]) : null))
      .filter(Boolean);
    if (keysNow.length === 0) return;

    const prevKeys = pending.prevKeys || new Set();
    let keyToFocus =
      pending.knownKey != null && pending.knownKey !== ""
        ? String(pending.knownKey)
        : null;
    if (!keyToFocus) {
      const novel = keysNow.filter((k) => !prevKeys.has(k));
      keyToFocus = novel.length ? novel[novel.length - 1] : null;
    }
    if (!keyToFocus) return;

    const grid = getDxDataGridInstance(dataGridRef);
    if (!grid) return;

    pendingInsertFocusRef.current = null;

    try {
      grid.option?.("focusedRowKey", keyToFocus);
    } catch {
      /* ignore */
    }
    if (typeof grid.navigateToRow === "function") {
      Promise.resolve(grid.navigateToRow(keyToFocus))
        .catch(() => undefined)
        .finally(() => {
          try {
            grid.selectRows?.([keyToFocus], false);
          } catch {
            /* ignore */
          }
        });
    } else {
      try {
        grid.selectRows?.([keyToFocus], false);
      } catch {
        /* ignore */
      }
    }

    if (onSelectedRowKeysChange) {
      onSelectedRowKeysChange([keyToFocus]);
    }
  }, [tableData, name, onSelectedRowKeysChange]);

  useEffect(() => {
    filterInitialAppliedRef.current = false;
  }, [initialFilterColumn?.value, tableData]);

  const handleContentReady = useCallback(
    (e) => {
      if (
        name !== "status-document" ||
        !initialFilterColumn?.value ||
        initialFilterColumn.value === ""
      ) {
        return;
      }
      if (filterInitialAppliedRef.current) return;
      const grid = e.component;
      grid.columnOption(initialFilterColumn.dataField, "filterValue", initialFilterColumn.value);
      grid.columnOption(initialFilterColumn.dataField, "selectedFilterOperation", "=");
      filterInitialAppliedRef.current = true;
      filterApplySuppressUntilRef.current = Date.now() + 500;
    },
    [name, initialFilterColumn],
  );

  const handleOptionChanged = useCallback(
    (e) => {
      if (!initialFilterColumn || !onInitialFilterOverridden) return;
      if (Date.now() < filterApplySuppressUntilRef.current) return;
      const n = e.name || "";
      const fn = String(e.fullName || "");
      if (!/filter/i.test(n) && !/filter/i.test(fn)) return;
      requestAnimationFrame(() => {
        try {
          const inst =
            e.component ||
            (typeof dataGridRef.current?.instance === "function"
              ? dataGridRef.current.instance()
              : null);
          if (!inst) return;
          const field = initialFilterColumn.dataField;
          const fv = inst.columnOption(field, "filterValue");
          const expected = String(initialFilterColumn.value ?? "");
          const actual = fv == null || fv === "" ? "" : String(fv);
          if (actual !== expected) {
            onInitialFilterOverridden();
          }
        } catch {
          /* ignore */
        }
      });
    },
    [initialFilterColumn, onInitialFilterOverridden],
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalEditOpen, setIsEditModalOpen] = useState(false);
  const [editData, setEditData] = useState({});
  const [isInputVisible, setIsInputVisible] = useState(false);
  const [id, setId] = useState({});

  const showDeleteConfirmation = (data) => {
    var currentDeleteSQL = selectedTable.deleteQuery;
    var idType = selectedTable.idType;

    var params = [];
    var parameterId = {
      Name: "anQId",
      Type: idType,
      Value: data[selectedTable.id],
    };
    params.push(parameterId);
    Swal.fire({
      title: t("common.confirmTitle"),
      text: t("common.confirmDelete"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: t("common.yesDelete"),
      cancelButtonText: t("common.no"),
    }).then((result) => {
      if (result.isConfirmed) {
        SettingsService.insertSQLQuery(currentDeleteSQL, params).then(
          (result) => {
            var data = result;

            if (data) {
              Swal.fire(t("common.deleted"), t("common.recordDeleted"), "success");
            } else {
              Swal.fire(t("common.error"), t("common.recordNotDeleted"), "error");
            }

            setTimeout(refresh, 1000);
          },
        );
      }
    });
  };

  const onDelete = (data) => {
    setId(data[selectedTable.id]);
    showDeleteConfirmation(data);
  };

  const onClose = () => {
    setIsModalOpen(false);
  };

  const onCloseEdit = () => {
    setIsEditModalOpen(false);
  };

  const generatePopupCreate = () => {
    setIsModalOpen(true);
  };

  const generatePopupEdit = (data) => {
    setEditData(data);
    setIsEditModalOpen(true);
  };
  const editRow = useCallback((e) => {
    var rowData = e.row.data
    onEdit(rowData)
    e.event.preventDefault();
  }, []);

  const deleteRow = useCallback((e) => {
    var rowData = e.row.data
    onDelete(rowData)
    e.event.preventDefault();
  }, []);

  const onAdd = () => {
    try {
      const selectedTable = tablesAssociation.find((table) => table.name === name);
      const keyField = selectedTable?.id;
      const rows = Array.isArray(tableData) ? tableData : [];
      const prevKeys = new Set(
        rows
          .map((r) => (r && keyField && r[keyField] != null ? String(r[keyField]) : null))
          .filter(Boolean),
      );
      lastKeysBeforeInsertRef.current = prevKeys;
      pendingInsertFocusRef.current = { prevKeys, knownKey: null };
    } catch {
      lastKeysBeforeInsertRef.current = null;
      pendingInsertFocusRef.current = { prevKeys: new Set(), knownKey: null };
    }
    generatePopupCreate(selectedTable);
  };

  const onEdit = (row) => {
    var target;
    var current = selectedTable.id;
    target = selectedTable.id;
    setId(row[current]);
    generatePopupEdit(row, selectedTable);
  };

  const systemColumns = useMemo(
    () => [
      {
        Header: (
          <button className="action-buttons white" title={t("common.entry")} onClick={onAdd}>
            <IoAddCircleSharp />
            {t("common.add")}
          </button>
        ),
        accessor: "actions",
        Cell: ({ row }) => (
          <div>
            <button
              className="action-buttons"
              title={t("common.deletion")}
              onClick={() => onDelete(row.original)}
            >
              <MdDeleteForever />
            </button>
            <button
              className="action-buttons"
              title={t("common.update")}
              onClick={() => onEdit(row.original)}
            >
              <MdEdit />
            </button>
          </div>
        ),
        type: "nothing",
      },
      {
        Header: "Naziv",
        accessor: "ID",
        additional: "",
        className: "name-column-system",
        type: "text",
        required: true,
        dbType: "String",
      },
      {
        Header: "Vrednost",
        accessor: "Value",
        additional: "",
        className: "value-column-system",
        type: "text",
        required: true,
        dbType: "String",
      },
    ],
    [t],
  );

  // This is the configuration for the subject codes //
  const subjectCodes = useMemo(
    () => [
      {
        Header: (
          <button className="action-buttons white" title={t("common.entry")} onClick={onAdd}>
            <IoAddCircleSharp />
            {t("common.add")}
          </button>
        ),
        accessor: "actions",

        Cell: ({ row }) => (
          <div>
            <button
              className="action-buttons"
              title={t("common.deletion")}
              onClick={() => onDelete(row.original)}
            >
              <MdDeleteForever />
            </button>
            <button
              className="action-buttons"
              title={t("common.update")}
              onClick={() => onEdit(row.original)}
            >
              <MdEdit />
            </button>
          </div>
        ),
        type: "nothing",
      },

      {
        Header: "Id",
        accessor: "anQId",
        className: "name-column-system",
        type: "nothing",
      },
      {
        Header: "Ident",
        accessor: "acIdent",
        className: "name-column-system",
        type: "dropdown",
        required: true,
        additional: "",
        sourceSelect: "SELECT acIdent, acName FROM tHE_SetItem;",
        columnOrder: ["acIdent", "acName"],
        columnOrderTranslation: ["Ident", "Naziv"],
        columnOrderWidth: [200, 300],
        dropdownId: "acIdent",
        dropdownPlaceholder: "",
        dropdownHelperField: "acName",
        dbType: "String",
      },
      {
        Header: "Subjekt",
        accessor: "acSubject",
        className: "name-column-system",
        type: "dropdown",
        additional: "",
        required: true,
        sourceSelect:
          "SELECT acSubject, acName2, acAddress, acPost, acCountry FROM tHE_SetSubj",
        columnOrder: [
          "acSubject",
          "acName2",
          "acAddress",
          "acPost",
          "acCountry",
        ],
        columnOrderTranslation: [
          "Subjekt",
          "Naziv",
          "Naslov",
          "Pošta",
          "Država",
        ],
        columnOrderWidth: [200, 300, 200, 200, 200],
        dropdownId: "acSubject",
        dropdownPlaceholder: "",
        dropdownHelperField: "acName2",
        dbType: "String",
      },
      {
        Header: "Črtna koda",
        accessor: "acCode",
        additional: "",
        required: true,
        max: 100,
        className: "name-column-system",
        type: "text",
        dbType: "String",
      },
      {
        Header: "Število kosov",
        accessor: "uWMSSerialNoBatch",
        additional: "",
        required: true,
        className: "name-column-system",
        type: "number",
        dbType: "Int64",
      },
    ],
    [t],
  );

  // This is the configuration for the statuses of documents //
  const statusDocument = useMemo(
    () => [
      {
        Header: (
          <button className="action-buttons white" title={t("common.entry")} onClick={onAdd}>
            <IoAddCircleSharp />
            {t("common.add")}
          </button>
        ),
        accessor: "actions",

        Cell: ({ row }) => (
          <div>
            <button
              className="action-buttons"
              title={t("common.deletion")}
              onClick={() => onDelete(row.original)}
            >
              <MdDeleteForever />
            </button>
            <button
              className="action-buttons"
              title={t("common.update")}
              onClick={() => onEdit(row.original)}
            >
              <MdEdit />
            </button>
          </div>
        ),
        type: "nothing",
      },
      {
        Header: "Id",
        accessor: "anQId",
        className: "name-column-system",
        type: "nothing",
      },
      {
        Header: "Vrsta dokumenta",
        accessor: "acDocType",
        max: 4,
        required: true,
        className: "name-column-system",
        type: "dropdown",
        additional: "",

        sourceSelect: "SELECT acDocType, acName FROM tPA_SetDocType;",
        columnOrder: ["acDocType", "acName"],
        columnOrderTranslation: ["Vrsta dokumenta", "Naziv"],
        columnOrderWidth: [200, 300],
        dropdownId: "acDocType",
        required: true,
        dropdownPlaceholder: "",
        dropdownHelperField: "acName",
        dbType: "String",
      },
      {
        Header: "Koda statusa",
        accessor: "acStatus",
        className: "name-column-system",
        type: "dropdown",
        sourceSelect: `SELECT 'T' AS vrednost UNION SELECT 'F' AS vrednost`,
        columnOrder: ["vrednost"],
        columnOrderTranslation: ["Vrednost"],
        columnOrderWidth: [200],
        dropdownId: "vrednost",
        required: true,
        dropdownPlaceholder: "",
        dropdownHelperField: "vrednost",
        additional: "",
        dbType: "String",
      },
      {
        Header: "Naziv statusa",
        accessor: "acName",
        className: "name-column-system",
        type: "text",
        additional: "",
        required: true,
        max: 20,
        dbType: "String",
      },
      {
        Header: "Viden",
        accessor: "uWMSShow",
        required: true,
        className: "name-column-system",
        type: "checkbox",
        additional: "",

        dbType: "Boolean",
        Cell: ({ row }) => (
          <input
            type="checkbox"
            checked={row.values.uWMSShow} // Set the checked state based on the value of uWMSShow
            onChange={() => {}}
          />
        ),
      },
    ],
    [t],
  );

  // This is the configuration for the statuses of documents //
  const documentTypes = useMemo(
    () => [
      {
        Header: (
          <button className="action-buttons white" title={t("common.entry")} onClick={onAdd}>
            <IoAddCircleSharp />
            {t("common.add")}
          </button>
        ),
        accessor: "actions",

        Cell: ({ row }) => (
          <div>
            <button
              className="action-buttons"
              title={t("common.deletion")}
              onClick={() => onDelete(row.original)}
            >
              <MdDeleteForever />
            </button>
            <button
              className="action-buttons"
              title={t("common.update")}
              onClick={() => onEdit(row.original)}
            >
              <MdEdit />
            </button>
          </div>
        ),
        type: "nothing",
      },
      {
        Header: "Id",
        accessor: "anQId",
        className: "name-column-system",
        additional: "",
        type: "nothing",
      },
      {
        Header: "Vrsta",
        accessor: "acDocType",
        required: true,
        className: "name-column-system",
        type: "dropdown",
        sourceSelect: "SELECT acDocType, acName FROM tPA_SetDocType;",
        columnOrder: ["acDocType", "acName"],
        columnOrderTranslation: ["Vrsta dokumenta", "Naziv"],
        columnOrderWidth: [200, 300],
        dropdownId: "acDocType",
        dropdownPlaceholder: "",
        dropdownHelperField: "acName",
        additional: "",
        dbType: "String",
      },
      {
        Header: "Namen",
        accessor: "acSetOf",
        className: "name-column-system",
        type: "dropdown",
        required: true,
        sourceSelect: `SELECT 'T' AS vrednost UNION SELECT 'F' AS vrednost`,
        columnOrder: ["vrednost"],
        columnOrderTranslation: ["Vrednost"],
        columnOrderWidth: [200],
        dropdownId: "vrednost",
        dropdownPlaceholder: "",
        additional: "single",
        dropdownHelperField: "vrednost",
        columnOrder: ["vrednost"],
        additional: "",
        dbType: "String",
      },
      {
        Header: "Varianta",
        accessor: "acType",
        className: "name-column-system",
        type: "dropdown",
        required: true,
        sourceSelect: `SELECT 'T' AS vrednost UNION SELECT 'F' AS vrednost`,
        columnOrder: ["vrednost"],
        columnOrderTranslation: ["Vrednost"],
        columnOrderWidth: [200],
        dropdownId: "vrednost",
        dropdownPlaceholder: "",
        additional: "single",
        dropdownHelperField: "vrednost",
        columnOrder: ["vrednost"],
        additional: "",
        dbType: "String",
      },
      {
        Header: "Naziv",
        accessor: "acName",
        max: 40,
        required: true,
        className: "name-column-system",
        type: "text",
        additional: "",
        dbType: "String",
      },
      {
        Header: "Izdajno",
        accessor: "acIssuer",
        className: "name-column-system",
        type: "text",
        required: true,
        max: 30,
        additional: "",
        dbType: "String",
      },
      {
        Header: "Prevzemno",
        accessor: "acReceiver",
        className: "name-column-system",
        required: true,
        type: "text",
        max: 30,
        additional: "",
        dbType: "String",
      },
      {
        Header: "Prevzeto",
        accessor: "acWarehouse",
        className: "name-column-system",
        type: "dropdown",
        required: true,
        sourceSelect: `select acSubject, acName2 from tHE_SetSubj where acWarehouse = 'T'`,
        columnOrder: ["acSubject", "acName2"],
        columnOrderTranslation: ["Subjekt", "Naziv"],
        columnOrderWidth: [200, 300],
        dropdownId: "acSubject",
        dropdownPlaceholder: "",
        dropdownHelperField: "acName2",
        additional: "",
        dbType: "String",
      },
      {
        Header: "Vrsta prevzema",
        accessor: "uWMSAcqDocType",
        className: "name-column-system",
        type: "dropdown",
        sourceSelect: `SELECT acDocType, acName FROM tPA_SetDocType WHERE acSetOf = 'F AND acType = 'P'`,
        columnOrder: ["acDocType", "acName"],
        columnOrderTranslation: ["Vrsta dokumenta", "Naziv"],
        columnOrderWidth: [200, 300],
        dropdownId: "acDocType",
        required: true,
        dropdownPlaceholder: "",
        dropdownHelperField: "acName",
        additional: "",
        dbType: "String",
      },
      {
        Header: "Vrsta izdaje",
        accessor: "uWMSIssueDocType",
        className: "name-column-system",
        type: "dropdown",
        sourceSelect: `SELECT acDocType, acName FROM tPA_SetDocType WHERE acSetOf = 'F' and acType = 'P'`,
        columnOrder: ["acDocType", "acName"],
        columnOrderTranslation: ["Vrsta dokumenta", "Naziv"],
        columnOrderWidth: [200, 300],
        dropdownId: "acDocType",
        required: true,
        dropdownPlaceholder: "",
        dropdownHelperField: "acName",
        additional: "",
        dbType: "String",
      },
      {
        Header: "Viden",
        accessor: "uWMS",
        className: "name-column-system",
        type: "checkbox",
        required: true,
        additional: "",
        dbType: "String",
      },
      {
        Header: "Status delno zakl.",
        accessor: "uWMSPartiallyFinishStatus",
        className: "name-column-system",
        type: "text",
        max: 5,
        required: true,
        additional: "",
        dbType: "String",
      },
      {
        Header: "Status zaklj.",
        accessor: "uWMSFinishStatus",
        className: "name-column-system",
        type: "text",
        required: true,
        max: 5,
        additional: "",
        dbType: "String",
      },
    ],
    [t],
  );

  const subjects = useMemo(
    () => [
      {
        Header: (
          <button className="action-buttons white" title={t("common.entry")} onClick={onAdd}>
            <IoAddCircleSharp />
            {t("common.add")}
          </button>
        ),
        accessor: "actions",

        Cell: ({ row }) => (
          <div>
            <button
              className="action-buttons"
              title={t("common.deletion")}
              onClick={() => onDelete(row.original)}
            >
              <MdDeleteForever />
            </button>
            <button
              className="action-buttons"
              title={t("common.update")}
              onClick={() => onEdit(row.original)}
            >
              <MdEdit />
            </button>
          </div>
        ),
        type: "nothing",
      },
      {
        Header: "Id",
        accessor: "anQId",
        className: "name-column-system",
        additional: "",
        type: "nothing",
      },
      {
        Header: "Šifra",
        accessor: "acSubject",
        className: "name-column-system",
        type: "text",
        max: 30,
        additional: "",
        dbType: "String",
        required: true,
      },
      {
        Header: "Kupec",
        accessor: "acBuyer",
        className: "name-column-system",
        type: "dropdown",
        sourceSelect: `SELECT 'T' AS vrednost UNION SELECT 'F' AS vrednost`,
        columnOrder: ["vrednost"],
        columnOrderTranslation: ["Vrednost"],
        columnOrderWidth: [200],
        dropdownId: "vrednost",
        dropdownPlaceholder: "",
        additional: "single",
        dropdownHelperField: "vrednost",
        columnOrder: ["vrednost"],
        dbType: "String",
        required: true,
      },
      {
        Header: "Dobavitelj",
        accessor: "acSupplier",
        className: "name-column-system",
        type: "dropdown",
        sourceSelect: `SELECT 'T' AS vrednost UNION SELECT 'F' AS vrednost`,
        columnOrder: ["vrednost"],
        columnOrderTranslation: ["Vrednost"],
        columnOrderWidth: [200],
        dropdownId: "vrednost",
        dropdownPlaceholder: "",
        additional: "single",
        dropdownHelperField: "vrednost",
        columnOrder: ["vrednost"],
        dbType: "String",
        required: true,
      },
      {
        Header: "Skladišče",
        accessor: "acWarehouse",
        type: "dropdown",
        sourceSelect: `SELECT 'T' AS vrednost UNION SELECT 'F' AS vrednost`,
        columnOrder: ["vrednost"],
        columnOrderTranslation: ["Vrednost"],
        columnOrderWidth: [200],
        dropdownId: "vrednost",
        dropdownPlaceholder: "",
        additional: "single",
        dropdownHelperField: "vrednost",
        className: "name-column-system",
        columnOrder: ["vrednost"],
        dbType: "String",
        required: true,
      },
      {
        Header: "Naziv",
        accessor: "acName2",
        className: "name-column-system",
        type: "text",
        max: 255,
        additional: "",
        dbType: "String",
        required: false,
      },
      {
        Header: "Naslov",
        accessor: "acAddress",
        max: 256,
        className: "name-column-system",
        type: "text",
        additional: "",
        dbType: "String",
        required: false,
      },
      {
        Header: "Pošta",
        accessor: "acPost",
        max: 13,
        className: "name-column-system",
        type: "text",
        additional: "",
        required: true,
        dbType: "String",
      },
      {
        Header: "Država",
        accessor: "acCountry",
        max: 60,
        className: "name-column-system",
        type: "text",
        additional: "",
        required: true,
        dbType: "String",
      },
      {
        Header: "Predpona",
        accessor: "acVATCodePrefix",
        className: "name-column-system",
        type: "text",
        max: 3,
        additional: "",
        required: true,
        dbType: "String",
        hideInGrid: true,
      },
      {
        Header: "Dav. št.",
        accessor: "acCode",
        max: 20,
        className: "name-column-system",
        type: "text",
        additional: "",
        required: true,
        dbType: "String",
      },
      {
        Header: "Mat. št.",
        accessor: "acRegNo",
        max: 20,
        className: "name-column-system",
        type: "text",
        additional: "",
        required: true,
        dbType: "String",
        hideInGrid: true,
      },
      {
        Header: "Aktiven",
        accessor: "acActive",
        className: "name-column-system",
        type: "dropdown",
        sourceSelect: `SELECT 'T' AS vrednost UNION SELECT 'F' AS vrednost`,
        columnOrder: ["vrednost"],
        columnOrderTranslation: ["Vrednost"],
        columnOrderWidth: [200],
        dropdownId: "vrednost",
        dropdownPlaceholder: "",
        dropdownHelperField: "vrednost",
        additional: "hidden-active single",
        required: false,
        dbType: "String",
      },
      {
        Header: "Zaloga",
        accessor: "uWMSStock",
        className: "name-column-system",
        type: "checkbox",
        additional: "hidden-active",
        required: true,
        dbType: "Boolean",
      },
      {
        Header: "Viden",
        accessor: "uWMS",
        className: "name-column-system",
        type: "checkbox",
        additional: "hidden-active",
        required: true,
        dbType: "Boolean",
      },
      {
        Header: "Brez naročila",
        accessor: "uWMSSubj",
        className: "name-column-system",
        type: "checkbox",
        required: true,
        additional: "hidden-active",
        dbType: "Boolean",
      },
    ],
    [t],
  );

  const idents = useMemo(
    () => [
      {
        Header: (
          <button className="action-buttons white" title={t("common.entry")} onClick={onAdd}>
            <IoAddCircleSharp />
            {t("common.add")}
          </button>
        ),

        accessor: "actions",
        Cell: ({ row }) => (
          <div>
            <button
              className="action-buttons"
              title={t("common.deletion")}
              onClick={() => onDelete(row.original)}
            >
              <MdDeleteForever />
            </button>
            <button
              className="action-buttons"
              title={t("common.update")}
              onClick={() => onEdit(row.original)}
            >
              <MdEdit />
            </button>
          </div>
        ),
        type: "nothing",
      },
      {
        Header: "Id",
        accessor: "anQId",
        className: "name-column-system",
        type: "nothing",
      },
      {
        Header: "Šifra identa",
        accessor: "acIdent",
        required: true,
        max: 16,
        className: "name-column-system",
        type: "text",
        dbType: "String",
      },
      {
        Header: "Naziv",
        max: 80,
        required: true,
        accessor: "acName",
        className: "name-column-system",
        type: "text",
        dbType: "String",
      },
      {
        Header: "EAN koda",
        accessor: "acCode",
        max: 50,
        required: true,
        className: "name-column-system",
        type: "text",
        dbType: "String",
        additional: "hidden-active",
      },
      {
        Header: "Tip identa",
        max: 3,
        accessor: "acSetOfItem",
        className: "name-column-system",
        type: "dropdown",
        sourceSelect: `SELECT acSetOfItem, acName FROM tHE_SetItemType`,
        columnOrder: ["acSetOfItem", "acName"],
        columnOrderTranslation: ["Šifra", "Naziv"],
        columnOrderWidth: [200, 300],
        dropdownId: "acSetOfItem",
        required: true,
        dropdownPlaceholder: "",
        additional: "hidden-active",
        dropdownHelperField: "acName",
        dbType: "String",
      },
      {
        max: 30,
        Header: "Dobavitelj",
        accessor: "acSupplier",
        className: "name-column-system",
        type: "dropdown",
        sourceSelect: `SELECT acSubject, acName2 FROM tHE_SetSubj where acSupplier = 'T'`,
        columnOrder: ["acSubject", "acName2"],
        columnOrderTranslation: ["Subjekt", "Naziv"],
        columnOrderWidth: [200, 300],
        dropdownId: "acSubject",
        required: true,
        additional: "hidden-active",
        dropdownPlaceholder: "",
        dropdownHelperField: "acName2",
        dbType: "String",
      },
      {
        Header: "1 enota",
        max: 3,
        accessor: "acUM",
        className: "name-column-system",
        type: "dropdown",
        sourceSelect: `SELECT acUM, acName FROM tHE_SetUM`,
        columnOrder: ["acUM", "acName"],
        columnOrderTranslation: ["Enota", "Naziv"],
        columnOrderWidth: [200, 300],
        dropdownId: "acUM",
        required: true,
        dropdownPlaceholder: "",
        dropdownHelperField: "acName",
        dbType: "String",
      },
      {
        Header: "Pretvornik",
        accessor: "anUMToUM2",
        max: 16,
        required: true,
        className: "name-column-system",
        type: "number",
        dbType: "String",
      },
      {
        Header: "2 enota",
        accessor: "acUM2",
        className: "name-column-system",
        type: "dropdown",
        sourceSelect: `SELECT acUM, acName FROM tHE_SetUM`,
        columnOrder: ["acUM", "acName"],
        columnOrderTranslation: ["Enota", "Naziv"],
        columnOrderWidth: [200, 300],
        additional: "hidden-active",
        required: true,
        dropdownId: "acUM",
        dropdownPlaceholder: "",
        dropdownHelperField: "acName",
        dbType: "String",
      },
      {
        Header: "Tip št.",
        accessor: "acSerialNo",
        additional: "hidden-active single",
        className: "name-column-system",
        type: "dropdown",
        sourceSelect: `SELECT 'T' AS vrednost UNION SELECT 'F' AS vrednost`,
        columnOrder: ["vrednost"],
        columnOrderTranslation: ["Vrednost"],
        required: true,
        columnOrderWidth: [200],
        dropdownId: "vrednost",
        dropdownPlaceholder: "",
        dropdownHelperField: "vrednost",
        dbType: "String",
      },
      {
        Header: "Aktiven",
        accessor: "acActive",
        className: "name-column-system",
        type: "dropdown",
        additional: "single",
        sourceSelect: `SELECT 'T' AS vrednost UNION SELECT 'F' AS vrednost`,
        columnOrder: ["vrednost"],
        columnOrderTranslation: ["Vrednost"],
        required: true,
        columnOrderWidth: [200],
        dropdownId: "vrednost",
        dropdownPlaceholder: "",
        dropdownHelperField: "vrednost",
        dbType: "String",
      },
      {
        Header: "Višina",
        accessor: "anDimHeight",
        className: "name-column-system",
        type: "number",
        required: true,
        dbType: "String",
      },
      {
        Header: "Širina",
        accessor: "anDimWidth",
        className: "name-column-system",
        type: "number",
        required: true,
        dbType: "String",
      },
      {
        Header: "Globina",
        accessor: "anDimDepth",
        className: "name-column-system",
        type: "number",
        required: true,
        dbType: "String",
      },
      {
        Header: "Teža",
        accessor: "anDimWeight",
        className: "name-column-system",
        type: "number",
        required: true,
        dbType: "String",
      },
      {
        Header: "Bruto",
        accessor: "anDimWeightBrutto",
        className: "name-column-system",
        type: "number",
        required: true,
        dbType: "String",
      },
      {
        Header: "En. dolžine",
        accessor: "acUMDim1",
        className: "name-column-system",
        type: "dropdown",
        sourceSelect: `SELECT acUM, acName FROM tHE_SetUM`,
        columnOrder: ["acUM", "acName"],
        columnOrderTranslation: ["Enota", "Naziv"],
        columnOrderWidth: [200, 300],
        dropdownId: "acUM",
        required: true,
        dropdownPlaceholder: "",
        dropdownHelperField: "acName",
        dbType: "String",
      },
      {
        Header: "En. teže",
        accessor: "acUMDim2",
        className: "name-column-system",
        type: "dropdown",
        sourceSelect: `SELECT acUM, acName FROM tHE_SetUM`,
        columnOrder: ["acUM", "acName"],
        columnOrderTranslation: ["Enota", "Naziv"],
        columnOrderWidth: [200, 300],
        dropdownId: "acDocType",
        required: true,
        dropdownPlaceholder: "",
        dropdownHelperField: "acName",
        dbType: "String",
      },
      {
        Header: "Vidno",
        accessor: "uWMS",
        required: true,
        className: "name-column-system",
        type: "checkbox",
        dbType: "String",
      },
    ],
    [t],
  );

  const tablesAssociation = [
    {
      name: "system",
      value: systemColumns,
      insertHasUser: false,
      insertUserId: "",
      updateHasUser: false,
      updateUserId: "",
      insertQuery:
        "INSERT INTO  [dbo].[uWMSSetting] (#fields) VALUES (#parameters)",
      deleteQuery: "DELETE FROM [dbo].[uWMSSetting] WHERE ID = @anQId",
      updateQuery: "UPDATE [dbo].[uWMSSetting] SET #update",
      id: "ID",
      idType: "String",
    },
    {
      name: "subject-codes",
      value: subjectCodes,
      insertHasUser: true,
      insertUserId: "anUserIns",
      updateHasUser: true,
      updateUserId: "anUserChg",
      insertQuery: `INSERT INTO [dbo].[tHE_SetItemExtItemSubj] (#fields) VALUES (#parameters)`,
      deleteQuery:
        "DELETE FROM [dbo].[tHE_SetItemExtItemSubj] WHERE [anQId] = @anQId",
      updateQuery: `UPDATE [dbo].[tHE_SetItemExtItemSubj] SET #update`,
      id: "anQId",
      idType: "Int64",
    },
    {
      name: "status-document",
      value: statusDocument,
      insertHasUser: true,
      insertUserId: "anUserIns",
      updateHasUser: true,
      updateUserId: "anUserChg",
      insertQuery: `INSERT INTO [dbo].[tPA_SetDocTypeStat] (#fields) VALUES (#parameters)`,
      deleteQuery:
        "DELETE FROM [dbo].[tPA_SetDocTypeStat] WHERE [anQId] = @anQId",
      updateQuery: `UPDATE [dbo].[tPA_SetDocTypeStat] SET #update`,
      id: "anQId",
      idType: "Int64",
    },
    {
      name: "type-document",
      value: documentTypes,
      insertHasUser: false,
      insertUserId: "",
      updateHasUser: false,
      updateUserId: "",
      insertQuery: `INSERT INTO (#fields) VALUES (#parameters)`,
      deleteQuery: "DELETE FROM [dbo].[tPA_SetDocType] WHERE [anQId] = @anQId`",
      updateQuery: `UPDATE [dbo].[tPA_SetDocType] SET #update`,
      id: "anQId",
      idType: "Int64",
    },
    {
      name: "subjects",
      value: subjects,
      insertQuery: `INSERT INTO [dbo].[tHE_SetSubj] (#fields) VALUES (#parameters)`,
      insertHasUser: true,
      insertUserId: "anUserIns",
      deleteQuery: "DELETE FROM [dbo].[tHE_SetSubj] WHERE [anQId] = @anQId",
      updateQuery: `UPDATE [dbo].[tHE_SetSubj] SET #update`,
      updateHasUser: true,
      updateUserId: "anUserChg",
      id: "anQId",
      idType: "Int64",
    },
    {
      name: "idents",
      value: idents,
      insertHasUser: true,
      insertUserId: "anUserIns",
      updateHasUser: true,
      updateUserId: "anUserChg",
      insertQuery: `INSERT INTO [dbo].[tHE_SetItem] (#fields) VALUES (#parameters)`,
      deleteQuery: "DELETE FROM [dbo].[tHE_SetItem] WHERE [anQId] = @anQId",
      updateQuery: `UPDATE [dbo].[tHE_SetItem] SET #update`,
      id: "anQId",
      idType: "Int64",
    },
  ];

  const generateColumns = () => {
    // Define your columns dynamically based on your data structure
    let columns = [];
    let selectedTable = tablesAssociation.find((table) => table.name === name);
    for (let i = 0; i < selectedTable.value.length; i++) {
      let currentRow = selectedTable.value[i];
      if(currentRow.type!=="nothing") {
        columns.push(
          <Column
            key={currentRow.accessor}
            dataField={currentRow.accessor}
            caption={typeof currentRow.Header === "string" ? trHeader(currentRow.Header, t) : currentRow.Header}
            visible={currentRow.hideInGrid ? false : true}
          />,
        );
      }
    }
    return columns;
  };



  var selectedTable = tablesAssociation.find((table) => table.name === name);

  const settingsExportColumnDefs = selectedTable.value
    .filter((c) => c.type !== "nothing")
    .map((c) => ({
      key: c.accessor,
      header:
        typeof c.Header === "string"
          ? trHeader(c.Header, t)
          : String(c.accessor),
    }));

  const handleSelectionChanged = useCallback(
    (e) => {
      onSelectionChanged?.(e);
      if (onSelectedRowKeysChange) {
        const keys =
          e.selectedRowKeys ??
          (typeof e.component?.getSelectedRowKeys === "function"
            ? e.component.getSelectedRowKeys()
            : []);
        onSelectedRowKeysChange(Array.isArray(keys) ? keys : []);
      }
    },
    [onSelectionChanged, onSelectedRowKeysChange],
  );

  const selectionProps =
    selectedRowKeysProp !== undefined
      ? { selectedRowKeys: selectedRowKeysProp }
      : {};

  return (
    <div className="global-react-table">
      <div className="filters positions wms-settings-grid-actions">
        <span className="actions smallerr" onClick={onAdd}>
          <span className="wms-action-label">{t("common.add")}</span>
          <IoAddCircleSharp />
        </span>
        <TableExportButton
          fileBaseName={`wms-settings-${name}`}
          columnDefs={settingsExportColumnDefs}
          getRows={() => getDxExportRows(dataGridRef, tableData)}
        />
      </div>
      <Insert
        refresh={refresh}
        onClose={onClose}
        onInserted={(payload) => {
          const prevKeys = lastKeysBeforeInsertRef.current || new Set();
          pendingInsertFocusRef.current = {
            prevKeys,
            knownKey: payload?.key ?? null,
          };
        }}
        selectedTable={selectedTable}
        isVisible={isModalOpen}
      />
      <Update
        id={id}
        data={editData}
        refresh={refresh}
        onClose={onCloseEdit}
        selectedTable={selectedTable}
        isVisible={isModalEditOpen}
      />





        <DataGrid
                  ref={dataGridRef}
                  className='devexpress-grid settings'
                  dataSource={tableData}
                  keyExpr={selectedTable["id"]}
                  allowColumnReordering={true}
                  allowColumnResizing={true}
                  noDataText={t("common.noData")}
                  columnAutoWidth={true}
                  columnHidingEnabled={true}
                  focusedRowEnabled={true}
                  hoverStateEnabled={true}
                  onContentReady={handleContentReady}
                  onOptionChanged={handleOptionChanged}
                  onSelectionChanged={handleSelectionChanged}
                  {...selectionProps}
                >
                <Editing
                    mode="row"
                    useIcons={true}
                    allowUpdating={true}
                    allowDeleting={true}     
                />
                  <FilterRow visible={true} />


                  <Column type="buttons" width={96}>
                    <Button name="edit" hoverStateEnabled={false} onClick={editRow} />
                    <Button name="delete" hoverStateEnabled={false} onClick={deleteRow} />
                  </Column>

                  {generateColumns()}

                  <Selection mode='single' />


</DataGrid>
      {gridBelow ? <div className="wms-tableforge-below">{gridBelow}</div> : null}
    </div>
  );
}

export default TableForge;
